import { MODEL_TOTAL } from '@/constants/modelInventory.generated';
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { View, ScrollView, StyleSheet, Pressable, Platform, Animated, Easing } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useDeckStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import useRequireAuth from '@/hooks/useRequireAuth';
import { BORDER_RADIUS } from '@/constants/config';

const isWeb = Platform.OS === 'web';

const MONO = Platform.select({
  ios:     'Menlo',
  android: 'monospace',
  default: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, Consolas, monospace',
});

/**
 * High-fidelity empty-state mock for the Deck route.
 * Multimodal production workspace: up to 12 columns, mixed model + tool types.
 */
function FadeIn({ delay = 0, children, style }) {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(8)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(opacity,    { toValue: 1, duration: 420, delay, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: 0, duration: 480, delay, useNativeDriver: true, easing: Easing.out(Easing.cubic) }),
    ]).start();
  }, [opacity, translateY, delay]);
  return (
    <Animated.View style={[{ opacity, transform: [{ translateY }] }, style]}>
      {children}
    </Animated.View>
  );
}

const MODALITIES = {
  chat:       { icon: 'message',          color: '#3B82F6', label: 'Text'        },
  image:      { icon: 'image',            color: '#A855F7', label: 'Image'       },
  video:      { icon: 'film',             color: '#F472B6', label: 'Video'       },
  music:      { icon: 'music',            color: '#10B981', label: 'Music'       },
  voice:      { icon: 'microphone',       color: '#F59E0B', label: 'Voice'       },
  transcribe: { icon: 'waveform-lines',   color: '#06B6D4', label: 'Transcribe'  },
  upscale:    { icon: 'up-right-and-down-left-from-center', color: '#EF4444', label: 'Upscale' },
  avatar:     { icon: 'user-tie',         color: '#8B5CF6', label: 'Avatar'      },
  sfx:        { icon: 'volume-high',      color: '#14B8A6', label: 'SFX'         },
  '3d':       { icon: 'cubes-stacked',    color: '#F97316', label: '3D'          },
  tool:       { icon: 'wand-magic-sparkles', color: '#6366F1', label: 'Tool'    },
};

// Maps mockup recipe IDs to backend DeckTemplate slugs (see
// backend/scripts/seed/seed_deck_templates.json). Keep these in sync —
// "Start with this recipe" passes the mapped slug to /json/decks.
const RECIPE_SLUG_MAP = {
  'music-video':    'music-video-factory',
  'podcast':        'podcast-post-production',
  'product-shots':  'product-photo-polish',
  'logo-sprint':    'logo-sprint',
  'social-reel':    'social-reel-pipeline',
  'voice-novel':    'audiobook-production',
  'brand-kit':      'brand-kit-in-a-day',
  'ad-creative':    'ad-creative-matrix',
  'news-brief':     'daily-news-brief',
  'game-assets':    'game-asset-factory',
  'talking-head':   'talking-head-video',
  'course-content': 'online-course-module',
  'llm-showdown':   'llm-showdown',
  'image-showdown': 'image-model-showdown',
  'storybook':      'illustrated-storybook',
  'investor-pitch': 'investor-pitch-deck',
  'game-asset-3d':  'game-asset-3d-forge',
  'arch-3d-massing': 'architecture-3d-massing',
  'product-3d-spin': 'product-3d-spin',
};

const PRODUCTION_PRESETS = [
  {
    id: 'music-video', title: 'Music video factory',
    body: 'Script → storyboard → moving clips → soundtrack → SFX',
    columns: [
      { type: 'chat',  label: 'Script (GPT-4o)' },
      { type: 'image', label: 'Storyboard (Imagen 4)' },
      { type: 'image', label: 'Frames (Flux Pro)' },
      { type: 'video', label: 'Motion (Kling 2.0)' },
      { type: 'music', label: 'Soundtrack (MiniMax Music)' },
      { type: 'sfx',   label: 'SFX (CassetteAI)' },
    ],
  },
  {
    id: 'podcast', title: 'Podcast post-production',
    body: 'Transcribe → tighten copy → cover art → intro music',
    columns: [
      { type: 'transcribe', label: 'Whisper' },
      { type: 'chat',       label: 'Edit pass' },
      { type: 'image',      label: 'Cover art' },
      { type: 'music',      label: 'Intro jingle' },
      { type: 'tool',       label: 'Audio cleanup' },
    ],
  },
  {
    id: 'product-shots', title: 'Product photo polish',
    body: 'Upload → background remove → relight → upscale → variants',
    columns: [
      { type: 'image',   label: 'Source' },
      { type: 'tool',    label: 'BG remove' },
      { type: 'tool',    label: 'Relight' },
      { type: 'upscale', label: 'Topaz' },
      { type: 'image',   label: 'Variant A' },
      { type: 'image',   label: 'Variant B' },
    ],
  },
  {
    id: 'logo-sprint', title: 'Logo sprint',
    body: 'Brief → four generators in parallel → upscale the winner',
    columns: [
      { type: 'chat',    label: 'Brief' },
      { type: 'image',   label: 'Ideogram' },
      { type: 'image',   label: 'Imagen 4' },
      { type: 'image',   label: 'Flux Pro' },
      { type: 'image',   label: 'Recraft' },
      { type: 'upscale', label: 'Topaz' },
    ],
  },
  {
    id: 'social-reel', title: 'Social reel pipeline',
    body: 'Hook copy → portrait video → captions → trend-matched music',
    columns: [
      { type: 'chat',       label: 'Hook copy' },
      { type: 'avatar',     label: 'Presenter (HeyGen)' },
      { type: 'transcribe', label: 'Auto-captions' },
      { type: 'music',      label: 'Trending audio (MiniMax Music)' },
      { type: 'tool',       label: 'Resize for Reels' },
    ],
  },
  {
    id: 'voice-novel', title: 'Audiobook production',
    body: 'Edit manuscript → narrate each chapter → mix with ambient score',
    columns: [
      { type: 'chat',  label: 'Proofread (GPT-4o)' },
      { type: 'voice', label: 'Ch. 1 narration' },
      { type: 'voice', label: 'Ch. 2 narration' },
      { type: 'music', label: 'Ambient score' },
      { type: 'tool',  label: 'Chapter merge' },
    ],
  },
  {
    id: 'brand-kit', title: 'Brand kit in a day',
    body: 'Color palette → wordmark → icon set → hero image → brand copy',
    columns: [
      { type: 'chat',    label: 'Brand brief' },
      { type: 'image',   label: 'Wordmark (Ideogram)' },
      { type: 'image',   label: 'Icon set (Flux)' },
      { type: 'image',   label: 'Hero image' },
      { type: 'upscale', label: 'Master export' },
    ],
  },
  {
    id: 'ad-creative', title: 'Ad creative matrix',
    body: 'Six visual variants × two copy angles — find the winner fast',
    columns: [
      { type: 'chat',  label: 'Copy angle A' },
      { type: 'chat',  label: 'Copy angle B' },
      { type: 'image', label: 'Visual 1 (Flux)' },
      { type: 'image', label: 'Visual 2 (Imagen)' },
      { type: 'image', label: 'Visual 3 (Recraft)' },
      { type: 'tool',  label: 'Compose & export' },
    ],
  },
  {
    id: 'news-brief', title: 'Daily news brief',
    body: 'Scrape sources → summarise → record voiceover → publish',
    columns: [
      { type: 'tool',       label: 'Web scraper' },
      { type: 'chat',       label: 'Summarise (o3)' },
      { type: 'voice',      label: 'Voiceover (ElevenLabs)' },
      { type: 'image',      label: 'Thumbnail' },
      { type: 'tool',       label: 'Publish to feed' },
    ],
  },
  {
    id: 'game-assets', title: 'Game asset factory',
    body: 'Concept art → sprite sheet → SFX pack → background music loop',
    columns: [
      { type: 'image',   label: 'Concept (Seedream)' },
      { type: 'image',   label: 'Sprite sheet (Flux)' },
      { type: 'upscale', label: '4× upscale' },
      { type: 'sfx',     label: 'SFX pack' },
      { type: 'music',   label: 'Loop (CassetteAI)' },
    ],
  },
  {
    id: 'talking-head', title: 'Talking-head video',
    body: 'Write script → generate avatar → add captions → dub in 5 languages',
    columns: [
      { type: 'chat',       label: 'Script (GPT-4o)' },
      { type: 'avatar',     label: 'Avatar (HeyGen)' },
      { type: 'transcribe', label: 'Captions' },
      { type: 'voice',      label: 'ES dub' },
      { type: 'voice',      label: 'FR dub' },
      { type: 'voice',      label: 'DE dub' },
    ],
  },
  {
    id: 'course-content', title: 'Online course module',
    body: 'Outline → slides → explainer video → quiz → thumbnail',
    columns: [
      { type: 'chat',   label: 'Outline (o3)' },
      { type: 'chat',   label: 'Slide copy' },
      { type: 'image',  label: 'Slide visuals' },
      { type: 'avatar', label: 'Explainer video' },
      { type: 'chat',   label: 'Quiz questions' },
      { type: 'image',  label: 'Thumbnail' },
    ],
  },
  {
    id: 'llm-showdown', title: 'LLM showdown',
    body: 'One prompt, five top language models — pick the best answer',
    columns: [
      { type: 'chat', label: 'GPT-4o' },
      { type: 'chat', label: 'Claude 3.5 Sonnet' },
      { type: 'chat', label: 'Gemini 3.1 Pro' },
      { type: 'chat', label: 'o3' },
      { type: 'chat', label: 'Kimi K2.6' },
    ],
  },
  {
    id: 'image-showdown', title: 'Image model showdown',
    body: 'Same prompt, five top image generators — see who wins your style',
    columns: [
      { type: 'image', label: 'Imagen 4' },
      { type: 'image', label: 'Flux Pro 1.1' },
      { type: 'image', label: 'Seedream 4.5' },
      { type: 'image', label: 'Ideogram 3' },
      { type: 'image', label: 'Recraft v3' },
    ],
  },
  {
    id: 'storybook', title: 'Illustrated storybook',
    body: 'Story idea → cover → four illustrated pages with consistent style',
    columns: [
      { type: 'chat',  label: 'Story (Claude)' },
      { type: 'image', label: 'Cover art' },
      { type: 'image', label: 'Page 1' },
      { type: 'image', label: 'Page 2' },
      { type: 'image', label: 'Page 3' },
      { type: 'image', label: 'Page 4' },
    ],
  },
  {
    id: 'investor-pitch', title: 'Investor pitch deck',
    body: 'Outline → key slide visuals → presenter video → talk track',
    columns: [
      { type: 'chat',   label: 'Pitch outline (o3)' },
      { type: 'image',  label: 'Slide hero visuals' },
      { type: 'image',  label: 'Diagram visuals' },
      { type: 'avatar', label: 'Presenter (HeyGen)' },
      { type: 'voice',  label: 'Talk track' },
      { type: 'tool',   label: 'Export to PDF' },
    ],
  },
  {
    id: 'game-asset-3d', title: 'Game asset 3D forge',
    body: 'Concept brief → reference art → 3D mesh → quad-mesh variant → PBR textures → item lore',
    columns: [
      { type: 'chat',  label: 'Asset brief' },
      { type: 'image', label: 'Concept art (Imagen 4)' },
      { type: 'image', label: 'Turnaround views (Flux)' },
      { type: '3d',    label: '3D mesh (Meshy v6)' },
      { type: '3d',    label: 'Quad mesh (Tripo H3.1)' },
      { type: 'tool',  label: 'PBR materials' },
    ],
  },
  {
    id: 'arch-3d-massing', title: 'Architecture 3D massing',
    body: 'Brief → site mood → massing study → 3D model → flythrough → narrative',
    columns: [
      { type: 'chat',  label: 'Design brief' },
      { type: 'image', label: 'Site context' },
      { type: 'image', label: 'Massing study' },
      { type: '3d',    label: '3D massing (Hunyuan)' },
      { type: 'video', label: 'Flythrough (Kling)' },
      { type: 'chat',  label: 'Architectural narrative' },
    ],
  },
  {
    id: 'product-3d-spin', title: 'Product 3D spin',
    body: 'Hero photo → background remove → 3D model → 360 spin video → listing copy',
    columns: [
      { type: 'image', label: 'Hero photo' },
      { type: 'tool',  label: 'BG remove' },
      { type: '3d',    label: '3D model (Meshy)' },
      { type: 'video', label: '360° spin' },
      { type: 'chat',  label: 'Listing copy' },
    ],
  },
];

function MockChatColumn({ tint }) {
  const { colors } = useTheme();
  return (
    <View style={pr.colInner}>
      <View style={[pr.userBubble, { backgroundColor: colors.solid_color_hover }]}>
        <View style={[pr.bubbleLine, { backgroundColor: colors.text_color_tertiary, width: '70%' }]} />
        <View style={[pr.bubbleLine, { backgroundColor: colors.text_color_tertiary, width: '40%' }]} />
      </View>
      <View style={pr.assistantBlock}>
        {[0.9, 0.85, 0.75, 0.6].map((w, i) => (
          <View key={i} style={[pr.line, { backgroundColor: colors.text_color_tertiary, width: `${w * 100}%`, opacity: 0.55 }]} />
        ))}
      </View>
      <View style={[pr.streamingDot, { backgroundColor: tint }]} />
    </View>
  );
}

function MockImageColumn() {
  const { colors } = useTheme();
  const grads = [
    ['#A855F7', '#3B82F6'],
    ['#F472B6', '#A855F7'],
    ['#10B981', '#06B6D4'],
    ['#F59E0B', '#EF4444'],
  ];
  return (
    <View style={pr.imgGrid}>
      {grads.map((g, i) => (
        <View key={i} style={[pr.imgTile, { borderColor: colors.border_color_secondary }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: `linear-gradient(135deg, ${g[0]}, ${g[1]})` }]} />
          ) : (
            <LinearGradient colors={g} style={StyleSheet.absoluteFill} />
          )}
        </View>
      ))}
    </View>
  );
}

function MockVideoColumn() {
  const { colors } = useTheme();
  return (
    <View style={pr.videoWrap}>
      <View style={[pr.videoThumb, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(160deg, #1e293b 0%, #0f172a 70%, #312e81 100%)' }]} />
        ) : (
          <LinearGradient colors={['#1e293b', '#0f172a', '#312e81']} style={StyleSheet.absoluteFill} />
        )}
        <View style={pr.playBtn}>
          <Icon name="play" size={16} color="#fff" solid />
        </View>
        <View style={pr.videoTimebar}>
          <View style={[pr.videoTimebarFill, { backgroundColor: '#fff' }]} />
        </View>
      </View>
      <View style={pr.videoMeta}>
        <Text size="xxs" color="tertiary" weight="medium">00:08 · 1080p</Text>
      </View>
    </View>
  );
}

function MockMusicColumn() {
  const { colors } = useTheme();
  const bars = 28;
  return (
    <View style={pr.musicWrap}>
      <View style={pr.waveform}>
        {[...Array(bars)].map((_, i) => {
          const h = 6 + Math.abs(Math.sin(i * 0.6)) * 30 + Math.abs(Math.cos(i * 1.3)) * 14;
          return (
            <View key={i} style={[pr.bar, { height: h, backgroundColor: i < 16 ? '#10B981' : colors.border_color_primary }]} />
          );
        })}
      </View>
      <View style={pr.musicMeta}>
        <Icon name="music" size={9} color={colors.text_color_tertiary} solid />
        <Text size="xxs" color="tertiary" weight="medium">2:14 · ambient · 92 BPM</Text>
      </View>
    </View>
  );
}

function MockTranscribeColumn() {
  const { colors } = useTheme();
  const lines = [
    { t: '00:02', w: '85%' },
    { t: '00:09', w: '70%' },
    { t: '00:14', w: '92%' },
    { t: '00:21', w: '60%' },
    { t: '00:28', w: '78%' },
  ];
  return (
    <View style={pr.transcribeWrap}>
      {lines.map((l, i) => (
        <View key={i} style={pr.transcribeRow}>
          <Text size="xxs" weight="bold" style={{ color: '#06B6D4', width: 36, fontFamily: MONO }}>{l.t}</Text>
          <View style={{ flex: 1, marginTop: 6 }}>
            <View style={[pr.line, { backgroundColor: colors.text_color_tertiary, width: l.w, opacity: 0.55, height: 4 }]} />
          </View>
        </View>
      ))}
    </View>
  );
}

function MockAvatarColumn() {
  return (
    <View style={pr.avatarWrap}>
      <View style={pr.avatarStage}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: 'radial-gradient(ellipse at 50% 35%, #fbbf2422 0%, transparent 60%), linear-gradient(180deg, #1c1917 0%, #0c0a09 100%)' }]} />
        ) : (
          <LinearGradient colors={['#1c1917', '#0c0a09']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[pr.avatarHead, { backgroundColor: '#8B5CF6' }]}>
          <Icon name="user-tie" size={18} color="#fff" solid />
        </View>
        <View style={[pr.recDot, { backgroundColor: '#ef4444' }]} />
      </View>
      <View style={pr.musicMeta}>
        <View style={[pr.recDotMini, { backgroundColor: '#ef4444' }]} />
        <Text size="xxs" color="tertiary" weight="medium">Speaking · HeyGen 4</Text>
      </View>
    </View>
  );
}

function MockVoiceColumn() {
  const { colors } = useTheme();
  const tint = MODALITIES.voice.color;
  return (
    <View style={pr.voiceWrap}>
      <View style={[pr.voiceStage, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 40%, ${tint}33 0%, transparent 65%)` }]} />
        ) : (
          <LinearGradient colors={[`${tint}33`, 'transparent']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[pr.voiceMic, { backgroundColor: tint }]}>
          <Icon name="microphone" size={16} color="#fff" solid />
        </View>
        <View style={pr.voiceWave}>
          {[...Array(22)].map((_, i) => {
            const h = 4 + Math.abs(Math.sin(i * 0.55)) * 22 + Math.abs(Math.cos(i * 0.95)) * 10;
            return <View key={i} style={{ width: 2, height: h, borderRadius: 1, backgroundColor: tint, opacity: i < 14 ? 0.85 : 0.35 }} />;
          })}
        </View>
      </View>
      <View style={pr.musicMeta}>
        <View style={[pr.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">0:42 · ElevenLabs Sage</Text>
      </View>
    </View>
  );
}

function MockUpscaleColumn() {
  const { colors } = useTheme();
  const tint = MODALITIES.upscale.color;
  return (
    <View style={pr.upscaleWrap}>
      <View style={pr.upscaleRow}>
        <View style={[pr.upscaleSmall, { borderColor: colors.border_color_secondary }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(135deg, #A855F7, #3B82F6)', filter: 'blur(2.5px)' }]} />
          ) : (
            <LinearGradient colors={['#A855F7', '#3B82F6']} style={StyleSheet.absoluteFill} />
          )}
          <View style={[pr.upscaleLabel, { backgroundColor: 'rgba(0,0,0,0.55)' }]}>
            <Text style={{ fontFamily: MONO, fontSize: 8, color: '#fff', letterSpacing: 0.3 }}>1×</Text>
          </View>
        </View>
        <View style={pr.upscaleArrow}>
          <Icon name="arrow-right" size={12} color={tint} solid />
        </View>
        <View style={[pr.upscaleLarge, { borderColor: tint, ...(isWeb ? { boxShadow: `0 0 0 2px ${tint}33` } : {}) }]}>
          {isWeb ? (
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(135deg, #A855F7, #3B82F6)' }]} />
          ) : (
            <LinearGradient colors={['#A855F7', '#3B82F6']} style={StyleSheet.absoluteFill} />
          )}
          <View style={[pr.upscaleLabel, { backgroundColor: tint }]}>
            <Text style={{ fontFamily: MONO, fontSize: 8, color: '#fff', letterSpacing: 0.3 }}>4×</Text>
          </View>
        </View>
      </View>
      <View style={[pr.upscaleProgress, { backgroundColor: `${tint}1f` }]}>
        <View style={{ height: 3, width: '78%', backgroundColor: tint, borderRadius: 1.5 }} />
      </View>
      <View style={pr.musicMeta}>
        <View style={[pr.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">512 → 2048 · 78%</Text>
      </View>
    </View>
  );
}

function Mock3DColumn() {
  const { colors } = useTheme();
  const tint = MODALITIES['3d'].color;
  return (
    <View style={pr.threeDWrap}>
      <View style={[pr.threeDStage, { borderColor: colors.border_color_secondary }]}>
        {isWeb ? (
          <>
            <View style={[StyleSheet.absoluteFill, { backgroundImage: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)' }]} />
            <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 42%, ${tint}40 0%, transparent 60%)` }]} />
            {/* CAD/blueprint floor grid */}
            <View
              pointerEvents="none"
              style={{
                position: 'absolute',
                left: -40,
                right: -40,
                top: '58%',
                bottom: -10,
                backgroundImage: `linear-gradient(${tint}26 1px, transparent 1px), linear-gradient(90deg, ${tint}26 1px, transparent 1px)`,
                backgroundSize: '14px 14px',
                opacity: 0.7,
                transform: 'perspective(140px) rotateX(58deg)',
                transformOrigin: 'center top',
              }}
            />
          </>
        ) : (
          <LinearGradient colors={['#0f172a', '#020617']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[pr.threeDCube, { backgroundColor: tint, ...(isWeb ? { boxShadow: `0 6px 26px ${tint}55, 0 0 0 4px ${tint}1f` } : {}) }]}>
          <Icon name="cubes-stacked" size={20} color="#fff" solid />
        </View>
        <View style={[pr.threeDPill, { backgroundColor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.18)' }]}>
          <Text style={{ fontFamily: MONO, fontSize: 8, color: '#fff', letterSpacing: 0.4 }}>GLB · 4MB</Text>
        </View>
      </View>
      <View style={pr.musicMeta}>
        <View style={[pr.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">12k tris · PBR · quad mesh</Text>
      </View>
    </View>
  );
}

function MockToolColumn() {
  const { colors } = useTheme();
  const tint = MODALITIES.tool.color;
  return (
    <View style={pr.toolWrap}>
      <View style={[pr.toolInputRow, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        <Icon name="image" size={10} color={colors.text_color_tertiary} solid />
        <Text size="xxs" color="tertiary" weight="medium" numberOfLines={1} style={{ flex: 1 }}>hero-shot.png</Text>
      </View>
      <View style={pr.toolArrow}>
        <Icon name="arrow-down" size={10} color={colors.text_color_tertiary} />
      </View>
      <View style={[pr.toolStage, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `radial-gradient(circle at 50% 40%, ${tint}33 0%, transparent 65%)` }]} />
        ) : (
          <LinearGradient colors={[`${tint}33`, 'transparent']} style={StyleSheet.absoluteFill} />
        )}
        <View style={[pr.toolBadge, { backgroundColor: tint }]}>
          <Icon name="wand-magic-sparkles" size={14} color="#fff" solid />
        </View>
        <View style={pr.toolProgressTrack}>
          <View style={[pr.toolProgressFill, { backgroundColor: tint, width: '64%' }]} />
        </View>
      </View>
      <View style={pr.musicMeta}>
        <View style={[pr.recDotMini, { backgroundColor: tint }]} />
        <Text size="xxs" color="tertiary" weight="medium">Remove background · 64%</Text>
      </View>
    </View>
  );
}

function AutoScrollView({ children, onComplete }) {
  const scrollRef = useRef(null);
  const posRef = useRef(0);
  const rafRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    if (!isWeb) {
      const fallback = setTimeout(() => onComplete && onComplete(), 9000);
      return () => clearTimeout(fallback);
    }
    const el = scrollRef.current;
    if (!el) return;

    const SPEED = 0.25;
    const INITIAL_HOLD_MS = 2400;
    const startTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let endHoldTimer = null;

    function fire() {
      if (completedRef.current) return;
      completedRef.current = true;
      onComplete && onComplete();
    }

    function tick() {
      const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const inInitialHold = now - startTime < INITIAL_HOLD_MS;
      if (!pausedRef.current && !completedRef.current && !inInitialHold && el) {
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) {
          if (!endHoldTimer) endHoldTimer = setTimeout(fire, 5500);
        } else {
          posRef.current += SPEED;
          if (posRef.current >= maxScroll) {
            posRef.current = maxScroll;
            el.scrollLeft = maxScroll;
            if (!endHoldTimer) endHoldTimer = setTimeout(fire, 1400);
          } else {
            el.scrollLeft = posRef.current;
          }
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    }

    function onEnter() {
      pausedRef.current = true;
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    }
    function onLeave() {
      resumeTimerRef.current = setTimeout(() => { pausedRef.current = false; }, 1200);
    }
    function onScroll() {
      posRef.current = el.scrollLeft;
    }

    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('scroll', onScroll, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resumeTimerRef.current);
      clearTimeout(endHoldTimer);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('scroll', onScroll);
    };
  }, [onComplete]);

  if (isWeb) {
    return (
      <div
        ref={scrollRef}
        className="deck-autoscroll"
        style={{
          display: 'flex',
          flexDirection: 'row',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <style>{`.deck-autoscroll::-webkit-scrollbar { display: none; }`}</style>
        {children}
      </div>
    );
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={pr.cols}>
      {children}
    </ScrollView>
  );
}

const PRODUCTION_DECKS = [
  {
    id: 'music-video',
    label: '"Music Video Factory"',
    columns: [
      { type: 'chat',       title: 'GPT-4o',        step: 'Write the script',     Component: MockChatColumn,       extra: { tint: '#3B82F6' } },
      { type: 'image',      title: 'Imagen 4',      step: 'Storyboard scenes',    Component: MockImageColumn },
      { type: 'video',      title: 'Kling 2.0',     step: 'Animate key frames',   Component: MockVideoColumn },
      { type: 'music',      title: 'MiniMax Music', step: 'Compose soundtrack',   Component: MockMusicColumn },
      { type: 'transcribe', title: 'Whisper Large', step: 'Sync captions',        Component: MockTranscribeColumn },
      { type: 'avatar',     title: 'HeyGen 4',      step: 'Render presenter',     Component: MockAvatarColumn },
      { type: 'tool',       title: 'BG Remove',     step: 'Clean background',     Component: MockToolColumn },
    ],
  },
  {
    id: 'brand-kit',
    label: '"Brand Launch Kit"',
    columns: [
      { type: 'chat',    title: 'Claude 3.5',  step: 'Brand brief',         Component: MockChatColumn,    extra: { tint: '#3B82F6' } },
      { type: 'image',   title: 'Ideogram 3',  step: 'Design wordmark',     Component: MockImageColumn },
      { type: 'image',   title: 'Flux Pro',    step: 'Generate icon set',   Component: MockImageColumn },
      { type: 'image',   title: 'Imagen 4',    step: 'Hero image',          Component: MockImageColumn },
      { type: 'chat',    title: 'GPT-4o',      step: 'Voice & tone copy',   Component: MockChatColumn,    extra: { tint: '#3B82F6' } },
      { type: 'upscale', title: 'Topaz',       step: '4× master export',    Component: MockUpscaleColumn },
    ],
  },
  {
    id: 'talking-head',
    label: '"Talking Head Video"',
    columns: [
      { type: 'chat',       title: 'o3',            step: 'Outline & script',     Component: MockChatColumn, extra: { tint: '#3B82F6' } },
      { type: 'avatar',     title: 'HeyGen 4',      step: 'Render presenter',     Component: MockAvatarColumn },
      { type: 'voice',      title: 'ElevenLabs',    step: 'Generate voiceover',   Component: MockVoiceColumn },
      { type: 'transcribe', title: 'Whisper Large', step: 'Auto-captions',        Component: MockTranscribeColumn },
      { type: 'music',      title: 'MiniMax Music', step: 'Background score',     Component: MockMusicColumn },
      { type: 'tool',       title: 'Final Cut',     step: 'Cut & export',         Component: MockToolColumn },
    ],
  },
  {
    id: 'product-shots',
    label: '"Product Photo Polish"',
    columns: [
      { type: 'image',   title: 'Source',     step: 'Original photo',     Component: MockImageColumn },
      { type: 'tool',    title: 'BG Remove',  step: 'Remove background',  Component: MockToolColumn },
      { type: 'tool',    title: 'Relight',    step: 'Relight scene',      Component: MockToolColumn },
      { type: 'upscale', title: 'Topaz',      step: '4× sharpen',         Component: MockUpscaleColumn },
      { type: 'image',   title: 'Flux Pro',   step: 'Lifestyle variant',  Component: MockImageColumn },
      { type: 'image',   title: 'Imagen 4',   step: 'Editorial variant',  Component: MockImageColumn },
    ],
  },
  {
    id: 'game-asset-3d',
    label: '"Game Asset 3D Forge"',
    columns: [
      { type: 'chat',  title: 'Claude 3.5',          step: 'Asset brief',          Component: MockChatColumn,  extra: { tint: '#3B82F6' } },
      { type: 'image', title: 'Imagen 4',            step: 'Concept art',          Component: MockImageColumn },
      { type: 'image', title: 'Flux Pro',            step: 'Turnaround views',     Component: MockImageColumn },
      { type: '3d',    title: 'Meshy v6',            step: 'Generate 3D mesh',     Component: Mock3DColumn },
      { type: '3d',    title: 'Tripo H3.1',          step: 'Quad-mesh variant',    Component: Mock3DColumn },
      { type: 'tool',  title: 'PBR Texture',         step: 'Bake materials',       Component: MockToolColumn },
      { type: 'chat',  title: 'GPT-4o',              step: 'Lore + item card',     Component: MockChatColumn,  extra: { tint: '#3B82F6' } },
    ],
  },
];

const RECIPE_CATEGORY = {
  'music-video':    'MUSIC & VIDEO',
  'podcast':        'PODCAST',
  'product-shots':  'PHOTOGRAPHY',
  'logo-sprint':    'BRAND DESIGN',
  'social-reel':    'SOCIAL MEDIA',
  'voice-novel':    'AUDIO',
  'brand-kit':      'BRAND DESIGN',
  'ad-creative':    'MARKETING',
  'news-brief':     'CONTENT',
  'game-assets':    'GAME DEV',
  'talking-head':   'VIDEO',
  'course-content': 'EDUCATION',
  'llm-showdown':   'COMPARE · LLM',
  'image-showdown': 'COMPARE · IMAGE',
  'storybook':      'ILLUSTRATION',
  'investor-pitch': 'BUSINESS',
  'game-asset-3d':  'GAME DEV · 3D',
  'arch-3d-massing': 'ARCHITECTURE · 3D',
  'product-3d-spin': 'PRODUCT · 3D',
};

function EditorialRecipeCard({ recipe, onUseRecipe, onCardPress, busy }) {
  const { colors } = useTheme();
  const a = MODALITIES[recipe.columns[0].type].color;
  const b = MODALITIES[recipe.columns[recipe.columns.length - 1].type].color;
  const category = RECIPE_CATEGORY[recipe.id] || 'WORKFLOW';
  return (
    <Pressable onPress={onCardPress} style={({ hovered }) => [v.cardBase, { backgroundColor: colors.solid_color_block, borderColor: hovered ? '#3b82f6' : colors.border_color_secondary, padding: 0, overflow: 'hidden' }]}>
      <View style={{ height: 4, position: 'relative', overflow: 'hidden' }}>
        {isWeb ? (
          <View style={[StyleSheet.absoluteFill, { backgroundImage: `linear-gradient(90deg, ${a}, ${b})` }]} />
        ) : (
          <LinearGradient colors={[a, b]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
        )}
      </View>
      <View style={{ padding: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <View style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: a }} />
          <Text weight="bold" color='tertiary' style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1.4 }}>
            {category}
          </Text>
        </View>
        <Text weight="bold" color="primary" style={{ fontSize: 16, lineHeight: 20, letterSpacing: -0.2 }}>
          {recipe.title}
        </Text>
        <Text size="xxs" color="secondary" style={{ marginTop: 4, lineHeight: 16 }} numberOfLines={2}>
          {recipe.body}
        </Text>
        <View style={[v.iconTrack, { marginTop: 12 }]}>
          {recipe.columns.map((col, j) => {
            const m = MODALITIES[col.type];
            return (
              <View key={j} style={[v.iconTile, { backgroundColor: `${m.color}1a`, borderColor: `${m.color}55` }]}>
                <Icon name={m.icon} size={9} color={m.color} solid />
              </View>
            );
          })}
        </View>
        <Pressable
          onPress={(e) => { e?.stopPropagation?.(); onUseRecipe?.(); }}
          disabled={!!busy}
          style={({ hovered }) => [
            v.ctaFilledFull,
            {
              backgroundColor: hovered ? `${a}EE` : `${a}22`,
              borderWidth: 1,
              borderColor: `${a}66`,
              marginTop: 14,
              opacity: busy ? 0.65 : 1,
            },
          ]}
        >
          {({ hovered }) => (
            <>
              <Text size="xxs" weight="bold" style={{ color: hovered ? '#fff' : a }}>
                {busy ? 'Creating deck…' : 'Start with this recipe'}
              </Text>
              <Icon name={busy ? 'spinner' : 'arrow-right'} size={9} color={hovered ? '#fff' : a} solid />
            </>
          )}
        </Pressable>
      </View>
    </Pressable>
  );
}

export default function DeckMockup() {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const router = useRouter();
  const { createDeck } = useDeckStore(state => state.actions);
  const decksLength = useDeckStore(state => state.decks.length);
  const limits = useDeckStore(state => state.limits);
  const requireAuth = useRequireAuth();
  const [deckIndex, setDeckIndex] = useState(0);
  const [busyRecipeId, setBusyRecipeId] = useState(null);
  const [busyBlank, setBusyBlank] = useState(false);
  const creatingRef = useRef(false);
  /** Width of the modality grid row — used so every card matches the 5-up slot width (last row does not stretch). */
  const [modalityGridWidth, setModalityGridWidth] = useState(0);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const transitioningRef = useRef(false);

  const atDeckLimit = decksLength >= (limits?.maxDecks || 12);

  const MODALITY_GRID_GAP = 12;
  const modalityColCount = isXS ? 2 : 5;
  const modalityCardWidth = useMemo(() => {
    if (modalityGridWidth <= 0) return null;
    const inner = modalityGridWidth;
    return (inner - MODALITY_GRID_GAP * (modalityColCount - 1)) / modalityColCount;
  }, [modalityGridWidth, modalityColCount]);

  /** Approximate equal slots before first onLayout (gap not modeled; real widths apply immediately after measure). */
  const modCardSizingFallback = useMemo(() => ({
    flexBasis: isXS ? '47%' : '18%',
    flexGrow: 0,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: isXS ? '47%' : '18%',
  }), [isXS]);

  // Both flows hit `createDeck` which is a per-user write — gate them
  // for anonymous visitors so the mockup hero stays fully browsable
  // while every "create" path funnels through the auth CTA.
  const handleStartBlank = useCallback(async () => {
    if (creatingRef.current || atDeckLimit) return;
    const ran = requireAuth(() => true, {
      source: 'deck_mockup_start_blank',
      title: 'Sign in to create a deck',
      subtitle: 'Decks save across devices so you can pick up your multi-column workspace anywhere.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    creatingRef.current = true;
    setBusyBlank(true);
    try {
      const slug = await createDeck({ name: 'Untitled deck' });
      if (slug) {
        router.push({ pathname: '/deck', params: { d: slug } });
      }
    } finally {
      creatingRef.current = false;
      setBusyBlank(false);
    }
  }, [atDeckLimit, createDeck, router, requireAuth]);

  const handleAllRecipes = useCallback(() => {
    router.push('/decks/recipes');
  }, [router]);

  const handleUseRecipe = useCallback(async (recipe) => {
    if (creatingRef.current || atDeckLimit) return;
    const ran = requireAuth(() => true, {
      source: 'deck_mockup_use_recipe',
      title: 'Sign in to use this recipe',
      subtitle: 'Recipes spin up a multi-column deck pre-wired with prompts — we save the deck to your account.',
      icon: 'layer-group',
    });
    if (ran !== true) return;
    creatingRef.current = true;
    setBusyRecipeId(recipe.id);
    try {
      const templateSlug = RECIPE_SLUG_MAP[recipe.id] || recipe.id;
      const slug = await createDeck({ name: recipe.title, templateSlug });
      if (slug) {
        router.push({ pathname: '/deck', params: { d: slug } });
      }
    } finally {
      creatingRef.current = false;
      setBusyRecipeId(null);
    }
  }, [atDeckLimit, createDeck, router, requireAuth]);

  const handleRecipeCardPress = useCallback((recipe) => {
    const slug = RECIPE_SLUG_MAP[recipe.id] || recipe.id;
    router.push(`/decks/recipes/${slug}`);
  }, [router]);

  const handleDeckComplete = useCallback(() => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      setDeckIndex((i) => (i + 1) % PRODUCTION_DECKS.length);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 380,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }).start(() => {
        transitioningRef.current = false;
      });
    });
  }, [fadeAnim]);

  const currentDeck = PRODUCTION_DECKS[deckIndex];

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: colors.solid_color_primary }}
      contentContainerStyle={[pr.scroll, { paddingHorizontal: isXS ? 16 : 28 }]}
    >
      <View style={pr.columnWrap}>
        <FadeIn delay={80}>
          <Text
            weight="bold"
            color="primary"
            style={{
              fontSize: isXS ? 30 : 46,
              lineHeight: isXS ? 36 : 54,
              letterSpacing: -0.7,
              textAlign: 'center',
              marginTop: 18,
              maxWidth: 720,
              alignSelf: 'center',
            }}
          >
            Every model.{'\n'}
            <Text weight="bold" color="secondary" style={{ fontSize: isXS ? 30 : 46, lineHeight: isXS ? 36 : 54, letterSpacing: -0.7 }}>
              Side by side.
            </Text>
          </Text>
        </FadeIn>

        <FadeIn delay={140}>
          <Text size="m" color="secondary" style={{ textAlign: 'center', marginTop: 14, maxWidth: 560, alignSelf: 'center', lineHeight: 24 }}>
            Run text, image, video, music, voice, and transcription models — plus one-click tools — side by side. Up to 12 columns of parallel work.
          </Text>
        </FadeIn>

        <FadeIn delay={180}>
          <View style={pr.actions}>
            <Pressable
              onPress={handleStartBlank}
              disabled={busyBlank || atDeckLimit}
              style={({ hovered }) => [
                pr.primary,
                {
                  backgroundColor: colors.text_color_primary,
                  opacity: (busyBlank || atDeckLimit) ? 0.6 : (hovered ? 0.9 : 1),
                },
              ]}
            >
              <Icon name={busyBlank ? 'spinner' : 'plus'} size={12} color={colors.solid_color_primary} solid />
              <Text size="s" weight="bold" style={{ color: colors.solid_color_primary }}>
                {busyBlank ? 'Creating…' : 'Start a blank deck'}
              </Text>
            </Pressable>
            <View style={[pr.actionSep, { backgroundColor: colors.border_color_secondary }]} />
            <Pressable
              onPress={handleAllRecipes}
              style={({ hovered }) => [pr.secondary, hovered && { backgroundColor: colors.solid_color_block }]}
            >
              <Text size="s" weight="medium" color="secondary">All recipes</Text>
              <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
            </Pressable>
          </View>
        </FadeIn>

        <FadeIn delay={220}>
          <View style={pr.frameWrap}>
            {isWeb && (
              <View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  top: 30, left: 30, right: 30, bottom: -20,
                  borderRadius: BORDER_RADIUS.large,
                  backgroundColor: '#3b82f6',
                  opacity: 0.10,
                  filter: 'blur(50px)',
                }}
              />
            )}
            <View
              style={[
                pr.frame,
                {
                  backgroundColor: colors.solid_color_block,
                  borderColor: colors.border_color_secondary,
                },
              ]}
            >
              <View style={[pr.chrome, { borderBottomColor: colors.border_color_secondary }]}>
                <View style={pr.dots}>
                  <View style={[pr.dot, { backgroundColor: '#ff5f57' }]} />
                  <View style={[pr.dot, { backgroundColor: '#febc2e' }]} />
                  <View style={[pr.dot, { backgroundColor: '#28c840' }]} />
                </View>
                <Animated.View style={[pr.urlPill, { backgroundColor: colors.solid_color_primary, borderColor: colors.border_color_secondary, opacity: fadeAnim }]}>
                  <Icon name="lock" size={9} color={colors.text_color_tertiary} solid />
                  <Text size="xxs" color="tertiary" weight="medium">gab.ai / deck — {currentDeck.label}</Text>
                </Animated.View>
                <Animated.View style={[pr.chromeRight, { opacity: fadeAnim }]}>
                  <Text size="xxs" color="tertiary" weight="medium" style={{ fontFamily: MONO }}>{currentDeck.columns.length} / 12</Text>
                </Animated.View>
              </View>

              <Animated.View style={{ opacity: fadeAnim }}>
                <AutoScrollView key={deckIndex} onComplete={handleDeckComplete}>
                  {currentDeck.columns.map((c, i) => {
                    const m = MODALITIES[c.type];
                    return (
                      <View
                        key={i}
                        style={[
                          pr.col,
                          {
                            borderRightColor: colors.border_color_secondary,
                            borderRightWidth: 1,
                          },
                        ]}
                      >
                        <View style={[pr.colHeader, { borderBottomColor: colors.border_color_secondary }]}>
                          <View style={[pr.colTypeBadge, { backgroundColor: `${m.color}1f` }]}>
                            <Icon name={m.icon} size={10} color={m.color} solid />
                          </View>
                          <View style={{ flex: 1, gap: 1 }}>
                            <Text size="xxs" weight="bold" color="primary" numberOfLines={1}>{c.step}</Text>
                            <Text style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 0.2 }} color='tertiary' numberOfLines={1}>{c.title}</Text>
                          </View>
                        </View>
                        <View style={pr.colBody}>
                          <c.Component {...(c.extra || {})} />
                        </View>
                      </View>
                    );
                  })}
                  <Pressable
                    style={({ hovered }) => [
                      pr.col,
                      pr.addCol,
                      {
                        borderColor: hovered ? '#3b82f6' : colors.border_color_secondary,
                        backgroundColor: hovered ? 'rgba(59, 130, 246, 0.06)' : 'transparent',
                      },
                    ]}
                  >
                    <View style={[pr.addColIcon, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]}>
                      <Icon name="plus" size={16} color={colors.text_color_tertiary} solid />
                    </View>
                    <Text size="xxs" weight="bold" color="secondary" style={{ marginTop: 10 }}>
                      Add a column
                    </Text>
                    <Text size="xxs" color="tertiary" style={{ textAlign: 'center', marginTop: 4, lineHeight: 14, paddingHorizontal: 18 }}>
                      Pick from {MODEL_TOTAL} models or one-click tools
                    </Text>
                  </Pressable>
                </AutoScrollView>
              </Animated.View>
            </View>

            <View style={pr.deckSwitcher}>
              {PRODUCTION_DECKS.map((d, i) => {
                const active = i === deckIndex;
                return (
                  <Pressable
                    key={d.id}
                    onPress={() => {
                      if (i === deckIndex || transitioningRef.current) return;
                      transitioningRef.current = true;
                      Animated.timing(fadeAnim, {
                        toValue: 0, duration: 220, easing: Easing.out(Easing.cubic), useNativeDriver: true,
                      }).start(() => {
                        setDeckIndex(i);
                        Animated.timing(fadeAnim, {
                          toValue: 1, duration: 360, easing: Easing.out(Easing.cubic), useNativeDriver: true,
                        }).start(() => { transitioningRef.current = false; });
                      });
                    }}
                    style={({ hovered }) => [
                      pr.deckChip,
                      {
                        backgroundColor: active ? colors.solid_color_block : 'transparent',
                        borderColor: active ? colors.text_color_primary : (hovered ? colors.border_color_primary : colors.border_color_secondary),
                      },
                    ]}
                  >
                    <View style={[pr.deckChipDot, { backgroundColor: active ? '#3b82f6' : colors.text_color_tertiary, opacity: active ? 1 : 0.5 }]} />
                    <Text size="xxs" weight={active ? 'bold' : 'medium'} color={active ? 'primary' : 'tertiary'}>
                      {d.label.replace(/"/g, '')}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={320}>
          <Text size="xs" weight="bold" color="tertiary" style={{ textTransform: 'uppercase', letterSpacing: 1.2, marginTop: 20, marginBottom: 25, marginLeft: 10 }}>
            Start with a recipe
          </Text>
          <View style={v.cardRow}>
            {PRODUCTION_PRESETS.map(r => (
              <EditorialRecipeCard
                key={r.id}
                recipe={r}
                busy={busyRecipeId === r.id}
                onUseRecipe={() => handleUseRecipe(r)}
                onCardPress={() => handleRecipeCardPress(r)}
              />
            ))}
          </View>
        </FadeIn>

        <FadeIn delay={340}>
          <View style={ls.recipesBridge}>
            <Pressable
              onPress={handleAllRecipes}
              accessibilityRole="button"
              accessibilityLabel="View all recipes"
              style={({ hovered }) => [
                ls.recipesBridgeBtn,
                {
                  backgroundColor: hovered ? colors.solid_color_block : colors.solid_color_primary,
                  borderColor: colors.border_color_secondary,
                },
              ]}
            >
              <Text size="s" weight="bold" color="primary">View all recipes</Text>
              <Icon name="arrow-right" size={11} color={colors.text_color_primary} solid />
            </Pressable>
          </View>
        </FadeIn>

        {/* ─────────────── SEO landing-style content sections ───────────────
            Long-form copy below the recipe grid. Doubles as onboarding context
            for new users and SEO ammunition (the same blocks are referenced by
            DeckTemplate.meta.pageCopy and DeckTemplateCategory.meta.pageCopy).
        */}
        <FadeIn delay={360}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              Why a deck?
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 26 : 34, lineHeight: isXS ? 32 : 42 }]}>
              Stop juggling tabs. Run the whole pipeline in one window.
            </Text>
            <Text size="m" color="secondary" style={ls.lede}>
              A deck is a side-by-side workspace for AI. Each column runs a different model, agent, or one-click tool — chat, image, video, music, voice, transcription, upscaling — and they all share the same context. Pin a deck per client, per campaign, per launch. Up to 12 columns. Multiple decks per account.
            </Text>
            <View style={ls.pillarsRow}>
              {[
                { icon: 'arrows-left-right',     title: 'Parallel by default',  copy: 'Compare GPT-4o vs Claude vs Gemini in three columns. Generate four image variants while a video model renders the hero shot.' },
                { icon: 'diagram-project',       title: 'One source of truth',  copy: 'The brief lives once, in column 1. Downstream columns reference it instead of re-pasting prompts across chats and DMs.' },
                { icon: 'floppy-disk',           title: 'Save the workflow',    copy: 'Like the layout? Save it as a recipe (or fork one of ours). The next launch starts on day three, not day one.' },
              ].map((p) => (
                <View key={p.title} style={[ls.pillarCard, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                  <View style={[ls.pillarIcon, { backgroundColor: 'rgba(59,130,246,0.12)' }]}>
                    <Icon name={p.icon} size={16} color="#3b82f6" solid />
                  </View>
                  <Text size="l" weight="bold" color="primary" style={{ marginTop: 12 }}>{p.title}</Text>
                  <Text size="n" color="secondary" style={{ marginTop: 6, lineHeight: 20 }}>{p.copy}</Text>
                </View>
              ))}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={400}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              Mix & match
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 24 : 30, lineHeight: isXS ? 30 : 38 }]}>
              Every modality, in one column-grid.
            </Text>
            <Text size="m" color="secondary" style={ls.lede}>
              Pick from {MODEL_TOTAL} models and one-click tools. Drop a column in, bind it to the model you want, give it a label. Reorder by drag-and-drop. The deck remembers everything across sessions.
            </Text>
            <View
              style={ls.modGrid}
              onLayout={({ nativeEvent }) => setModalityGridWidth(nativeEvent.layout.width)}
            >
              {[
                { key: 'chat',       label: 'Text & chat',     copy: 'GPT, Claude, Gemini, DeepSeek, Qwen, Kimi, Arya. Reasoning, drafting, code, JSON.' },
                { key: 'image',      label: 'Image',           copy: 'Imagen, Flux, Ideogram, Recraft, Seedream, Nano Banana. Photo, vector, type, brand.' },
                { key: 'video',      label: 'Video',           copy: 'Kling, Wan, Seedance, Veo. Text-to-video, image-to-video, motion frames.' },
                { key: 'music',      label: 'Music',           copy: 'MiniMax Music, CassetteAI. Songs, beds, intros, loops, branded jingles.' },
                { key: 'voice',      label: 'Voice & TTS',     copy: 'ElevenLabs, OpenAI TTS. Narration, dubs, multi-language voiceover.' },
                { key: 'transcribe', label: 'Transcribe',      copy: 'Whisper, ElevenLabs Scribe. Captions, edits, podcast clean-up.' },
                { key: 'upscale',    label: 'Upscale & polish',copy: 'Topaz, restoration, background removal, relighting, format conversion.' },
                { key: '3d',         label: '3D objects',      copy: 'Meshy, Tripo3D, Hunyuan 3D. Text- or image-to-3D meshes (GLB) with PBR textures — game-ready, AR-viewable.' },
                { key: 'tool',       label: 'One-click tools', copy: 'Headshots, logos, image edits, slides — bookmark them as columns.' },
              ].map((m) => {
                const meta = MODALITIES[m.key] || MODALITIES.tool;
                const slot =
                  modalityCardWidth != null
                    ? {
                      width: modalityCardWidth,
                      maxWidth: modalityCardWidth,
                      flexBasis: modalityCardWidth,
                      flexGrow: 0,
                      flexShrink: 0,
                    }
                    : modCardSizingFallback;
                return (
                  <View key={m.key} style={[ls.modCard, slot, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
                    <View style={[ls.modIcon, { backgroundColor: `${meta.color}1f`, borderColor: `${meta.color}55` }]}>
                      <Icon name={meta.icon} size={14} color={meta.color} solid />
                    </View>
                    <Text size="m" weight="bold" color="primary">{m.label}</Text>
                    <Text size="s" color="secondary" style={{ marginTop: 4, lineHeight: 18 }}>{m.copy}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={440}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              How it works
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 24 : 30, lineHeight: isXS ? 30 : 38 }]}>
              From blank deck to shipped asset in five steps.
            </Text>
            <View style={ls.steps}>
              {[
                { title: 'Pick a recipe (or start blank)', copy: 'Browse the catalog at gab.ai/decks/recipes. Cloning copies the column layout into a fresh deck under your account in one click.' },
                { title: 'Write the brief in column 1',     copy: 'Most recipes pin column 1 as the brief: a chat thread that holds the goal, audience, constraints, and reference links.' },
                { title: 'Fan out into parallel columns',   copy: 'Add image, video, music, or tool columns referencing the brief. They all run independently — no waiting on a single model.' },
                { title: 'Compare, pick winners, iterate',  copy: 'Hover any column header to drag-resize, focus, or duplicate. Lock the winners. Re-prompt the rest. Stack a polish/upscale column when you’re close.' },
                { title: 'Save the deck (or its recipe)',   copy: 'Decks auto-save and sync across devices. If the layout proved itself, save it as a recipe so the next campaign starts on third base.' },
              ].map((step, i) => (
                <View key={step.title} style={[ls.stepCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
                  <View style={[ls.stepNum, { backgroundColor: 'rgba(59,130,246,0.12)' }]}>
                    <Text size="s" weight="bold" style={{ color: '#3b82f6' }}>{i + 1}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text size="l" weight="bold" color="primary">{step.title}</Text>
                    <Text size="n" color="secondary" style={{ marginTop: 6, lineHeight: 22 }}>{step.copy}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={480}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              Who uses decks
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 24 : 30, lineHeight: isXS ? 30 : 38 }]}>
              The deck pattern fits anyone shipping AI assets at pace.
            </Text>
            <View style={ls.checkGrid}>
              {[
                'Solo creators producing weekly videos, podcasts, and shorts',
                'Brand designers running logo and key-art sprints across four image models',
                'Content teams building newsletters, social calendars, and ad creative in parallel',
                'Indie game devs pumping out concept art, sprites, SFX, and music loops',
                'E-commerce sellers polishing product photography, descriptions, and ads in one pass',
                'Founders drafting investor decks, talk tracks, and explainer videos before a pitch',
                'Educators turning a syllabus into slides, scripts, narration, and quiz questions',
                'Marketers benchmarking GPT-4o vs Claude vs Gemini for the same brief',
                'Agencies spinning up a deck per client and saving the layout as a repeatable recipe',
                'Anyone tired of copy-pasting prompts between five different ChatGPT tabs',
              ].map((line) => (
                <View key={line} style={ls.checkRow}>
                  <View style={[ls.checkBadge, { backgroundColor: 'rgba(16,185,129,0.15)' }]}>
                    <Icon name="check" size={12} color="#10B981" solid />
                  </View>
                  <Text size="m" color="primary" style={{ flex: 1, lineHeight: 22 }}>{line}</Text>
                </View>
              ))}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={520}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              Tabs vs decks
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 24 : 30, lineHeight: isXS ? 30 : 38 }]}>
              The before/after most teams describe.
            </Text>
            <View style={ls.compareRow}>
              <View style={[ls.compareCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
                <Text size="xs" weight="bold" style={{ color: '#ef4444', textTransform: 'uppercase', letterSpacing: 1.2 }}>BEFORE</Text>
                <Text size="m" weight="bold" color="primary" style={{ marginTop: 8 }}>Five tabs, three logins, no source of truth.</Text>
                <View style={{ marginTop: 12, gap: 8 }}>
                  {[
                    'Brief lives in a Notion page someone forgot to update.',
                    'Image prompts get re-typed into four different generators.',
                    'Video model is on a separate site with a different login.',
                    'Voice and music live in yet another tab. SFX is a fifth.',
                    'Final asset hand-off is a Loom video and a Slack thread.',
                  ].map((line) => (
                    <View key={line} style={ls.compareRowItem}>
                      <Icon name="xmark" size={14} color="#ef4444" />
                      <Text size="m" color="secondary" style={{ flex: 1, lineHeight: 22 }}>{line}</Text>
                    </View>
                  ))}
                </View>
              </View>
              <View style={[ls.compareCard, { borderColor: '#10B98166', backgroundColor: 'rgba(16,185,129,0.05)' }]}>
                <Text size="xs" weight="bold" style={{ color: '#10B981', textTransform: 'uppercase', letterSpacing: 1.2 }}>AFTER</Text>
                <Text size="m" weight="bold" color="primary" style={{ marginTop: 8 }}>One deck, twelve columns, every step visible.</Text>
                <View style={{ marginTop: 12, gap: 8 }}>
                  {[
                    'Brief is column 1; every other column references it directly.',
                    'Image, video, music, and tool columns run side by side.',
                    'Compare model outputs without losing the original prompt.',
                    'Drag to reorder. Resize. Duplicate the winners. Cut the rest.',
                    'Save the deck as a recipe — the next launch starts already wired.',
                  ].map((line) => (
                    <View key={line} style={ls.compareRowItem}>
                      <Icon name="check" size={14} color="#10B981" />
                      <Text size="m" color="secondary" style={{ flex: 1, lineHeight: 22 }}>{line}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={560}>
          <View style={ls.section}>
            <Text size="xs" weight="bold" color="tertiary" style={ls.eyebrow}>
              FAQ
            </Text>
            <Text weight="bold" color="primary" style={[ls.h2, { fontSize: isXS ? 24 : 30, lineHeight: isXS ? 30 : 38 }]}>
              Common questions about Gab AI Deck.
            </Text>
            <View style={{ gap: 12, marginTop: 18 }}>
              {[
                {
                  q: 'What is a Gab AI Deck?',
                  a: 'A deck is a multi-column workspace where each column runs a different AI model, agent, or one-click tool. You can mix text, image, video, music, voice, transcription, and upscaling columns in the same window — up to 12 per deck — and keep multiple decks per account.',
                },
                {
                  q: 'How is a deck different from a regular AI chat?',
                  a: 'A chat is one conversation. A deck is a workflow. Columns reference the same brief, run in parallel, and stay pinned across sessions. You can compare model outputs, chain stages of a production pipeline, or split a project across modalities without losing context.',
                },
                {
                  q: 'What are deck recipes?',
                  a: 'Pre-built deck layouts you can clone in one click. Each recipe ships a column layout (e.g., Script → Storyboard → Frames → Motion → Soundtrack), default model bindings, and a description of the workflow. Browse the full catalog at /decks/recipes.',
                },
                {
                  q: 'Do recipes cost extra?',
                  a: 'No. Cloning a recipe is included with your account — it just copies a layout into your workspace. Individual model and tool runs may consume credits per your Gab AI plan, the same as anywhere else in the app.',
                },
                {
                  q: 'Can I save my own deck as a recipe?',
                  a: 'Yes. Once you have a deck wired the way you like, save it. The next campaign or launch can start from the saved layout instead of from scratch.',
                },
                {
                  q: 'How many decks and columns can I create?',
                  a: 'Up to 12 columns per deck and multiple decks per account. The exact deck cap depends on your plan; the app surfaces a clear limit when you hit it.',
                },
                {
                  q: 'Does Gab AI Deck work on mobile?',
                  a: 'Yes — the deck adapts to narrower viewports, but multi-column production work feels best on a wide screen. Mobile is great for reviewing runs and triggering recipe clones.',
                },
                {
                  q: 'Which models can I bind to a column?',
                  a: `All of Gab AI’s ${MODEL_TOTAL} models and tools — GPT, Claude, Gemini, DeepSeek, Qwen, Arya, o3, Imagen, Flux, Ideogram, Recraft, Kling, Wan, ElevenLabs, Whisper, and dozens more. Switch models on a column at any time without rebuilding the deck.`,
                },
              ].map((item) => (
                <View key={item.q} style={[ls.faqCard, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_block }]}>
                  <View style={ls.faqQRow}>
                    <Icon name="circle-question" size={15} color={colors.text_color_primary} />
                    <Text size="l" weight="bold" color="primary" style={{ flex: 1, lineHeight: 22 }}>{item.q}</Text>
                  </View>
                  <Text size="n" color="secondary" style={{ marginTop: 8, lineHeight: 22 }}>{item.a}</Text>
                </View>
              ))}
            </View>
          </View>
        </FadeIn>

        <FadeIn delay={600}>
          <View style={[ls.bottomCta, { backgroundColor: colors.solid_color_block, borderColor: colors.border_color_secondary }]}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text weight="bold" color="primary" style={{ fontSize: 22, lineHeight: 28, letterSpacing: -0.4 }}>
                Open your first deck.
              </Text>
              <Text size="n" color="secondary" style={{ marginTop: 6, maxWidth: 480, lineHeight: 22 }}>
                Start blank, or fork one of the recipes above. Either way, you can rename, swap, and rebind columns until the layout fits the work.
              </Text>
            </View>
            <View style={ls.bottomCtaActions}>
              <Pressable
                onPress={handleStartBlank}
                disabled={busyBlank || atDeckLimit}
                style={({ hovered }) => [
                  pr.primary,
                  {
                    backgroundColor: colors.text_color_primary,
                    opacity: (busyBlank || atDeckLimit) ? 0.6 : (hovered ? 0.9 : 1),
                  },
                ]}
              >
                <Icon name={busyBlank ? 'spinner' : 'plus'} size={12} color={colors.solid_color_primary} solid />
                <Text size="s" weight="bold" style={{ color: colors.solid_color_primary }}>
                  {busyBlank ? 'Creating…' : 'Start a blank deck'}
                </Text>
              </Pressable>
              <Pressable
                onPress={handleAllRecipes}
                style={({ hovered }) => [pr.secondary, hovered && { backgroundColor: colors.solid_color_primary }]}
              >
                <Text size="s" weight="medium" color="secondary">All recipes</Text>
                <Icon name="arrow-right" size={10} color={colors.text_color_secondary} />
              </Pressable>
            </View>
          </View>
        </FadeIn>

        <AppDashboardFooter />
      </View>
    </ScrollView>
  );
}

const PR_COL_W = 220;

const pr = StyleSheet.create({
  scroll: { flexGrow: 1, paddingBottom: 80, alignItems: 'center' },
  columnWrap: { width: '100%', maxWidth: 1180 },
  eyebrow: { flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center' },
  eyebrowDot: { width: 6, height: 6, borderRadius: 3 },

  frameWrap: { marginTop: 30, marginBottom: 8, position: 'relative' },
  frame: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.large,
    overflow: 'hidden',
    ...(isWeb ? { boxShadow: '0 24px 60px rgba(0,0,0,0.10)' } : {}),
  },
  chrome: {
    height: 38,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    borderBottomWidth: 1,
  },
  dots: { flexDirection: 'row', gap: 6, width: 70 },
  dot: { width: 10, height: 10, borderRadius: 5 },
  urlPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  chromeRight: { width: 70, alignItems: 'flex-end' },

  cols: { flexDirection: 'row' },
  col: { width: PR_COL_W, height: 280, flexShrink: 0 },
  addCol: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderStyle: 'dashed',
    ...(isWeb ? { transition: 'background-color 0.18s ease, border-color 0.18s ease', cursor: 'pointer' } : {}),
  },
  addColIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 10,
    paddingVertical: 9,
    borderBottomWidth: 1,
  },
  colTypeBadge: {
    width: 18,
    height: 18,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  colBody: { flex: 1, padding: 10 },

  colInner: { flex: 1, gap: 10, position: 'relative' },
  userBubble: {
    alignSelf: 'flex-end',
    maxWidth: '90%',
    paddingHorizontal: 9,
    paddingVertical: 7,
    borderRadius: 8,
    borderBottomRightRadius: 3,
    gap: 4,
  },
  bubbleLine: { height: 4, borderRadius: 2 },
  assistantBlock: { gap: 5 },
  line: { height: 4, borderRadius: 2 },
  streamingDot: {
    position: 'absolute',
    bottom: 4,
    left: 4,
    width: 6,
    height: 6,
    borderRadius: 3,
  },

  imgGrid: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  imgTile: {
    flexBasis: '47%',
    flexGrow: 1,
    aspectRatio: 1,
    borderRadius: 6,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },

  videoWrap: { flex: 1, gap: 8 },
  videoThumb: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1.5,
    borderColor: '#ffffff66',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoTimebar: {
    position: 'absolute',
    bottom: 6,
    left: 8,
    right: 8,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  videoTimebarFill: { width: '38%', height: 2, borderRadius: 1 },
  videoMeta: { flexDirection: 'row', alignItems: 'center', gap: 5 },

  musicWrap: { flex: 1, gap: 10, justifyContent: 'space-between' },
  waveform: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  bar: { flex: 1, borderRadius: 1.5, minHeight: 4 },

  transcribeWrap: { flex: 1, gap: 8 },
  transcribeRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },

  avatarWrap: { flex: 1, gap: 8 },
  avatarStage: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  avatarHead: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 4px 16px rgba(0,0,0,0.4)' } : {}),
  },
  recDot: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 6,
    height: 6,
    borderRadius: 3,
    ...(isWeb ? { boxShadow: '0 0 0 3px rgba(239,68,68,0.25)' } : {}),
  },
  recDotMini: { width: 5, height: 5, borderRadius: 3 },

  toolWrap: { flex: 1, gap: 6 },
  toolInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  toolArrow: { alignItems: 'center', paddingVertical: 1 },
  toolStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    paddingHorizontal: 12,
  },
  toolBadge: {
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 6px 18px rgba(99,102,241,0.35)' } : {}),
  },
  toolProgressTrack: {
    position: 'absolute',
    bottom: 8,
    left: 10,
    right: 10,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: 'rgba(99,102,241,0.18)',
    overflow: 'hidden',
  },
  toolProgressFill: { height: 3, borderRadius: 1.5 },

  voiceWrap: { flex: 1, gap: 8 },
  voiceStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    gap: 12,
    paddingHorizontal: 12,
  },
  voiceMic: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    ...(isWeb ? { boxShadow: '0 4px 14px rgba(245,158,11,0.35)' } : {}),
  },
  voiceWave: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    height: 30,
  },

  upscaleWrap: { flex: 1, gap: 10, justifyContent: 'space-between' },
  upscaleRow: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  upscaleSmall: {
    width: 38,
    height: 38,
    borderRadius: 5,
    borderWidth: 1,
    overflow: 'hidden',
    position: 'relative',
  },
  upscaleArrow: { paddingHorizontal: 2 },
  upscaleLarge: {
    width: 78,
    height: 78,
    borderRadius: 7,
    borderWidth: 1.5,
    overflow: 'hidden',
    position: 'relative',
  },
  upscaleLabel: {
    position: 'absolute',
    bottom: 3,
    right: 3,
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 3,
  },
  upscaleProgress: {
    height: 3,
    borderRadius: 1.5,
    overflow: 'hidden',
  },

  threeDWrap: { flex: 1, gap: 8 },
  threeDStage: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  threeDCube: {
    width: 56,
    height: 56,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '-6deg' }],
  },
  threeDPill: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },

  deckSwitcher: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginTop: 14,
  },
  deckChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    ...(isWeb ? { transition: 'background-color 0.18s ease, border-color 0.18s ease', cursor: 'pointer' } : {}),
  },
  deckChipDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    ...(isWeb ? { transition: 'background-color 0.18s ease, opacity 0.18s ease' } : {}),
  },

  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 26,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  primary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 18,
    paddingVertical: 11,
    borderRadius: BORDER_RADIUS.medium,
    ...(isWeb ? { transition: 'opacity 0.15s ease' } : {}),
  },
  actionSep: { width: 1, height: 18, marginHorizontal: 10 },
  secondary: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: BORDER_RADIUS.medium,
    ...(isWeb ? { transition: 'background-color 0.15s ease' } : {}),
  },
});

const v = StyleSheet.create({
  cardRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
  },
  cardBase: {
    flexBasis: 240,
    flexGrow: 1,
    maxWidth: 280,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.medium,
    padding: 16,
    ...(isWeb ? { transition: 'border-color 0.15s ease', cursor: 'pointer' } : {}),
  },
  iconTrack: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 10,
    flexWrap: 'wrap',
  },
  iconTile: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaFilledFull: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 9,
    borderRadius: BORDER_RADIUS.small,
    ...(isWeb ? { transition: 'background-color 0.15s ease', cursor: 'pointer' } : {}),
  },
});

// Landing-style sections rendered below the recipe grid on the empty deck page.
// Mirrors the "deck as a workflow surface" pitch on /decks/recipes/[slug] body.
const ls = StyleSheet.create({
  section: {
    marginTop: 60,
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
  },
  eyebrow: {
    textTransform: 'uppercase',
    letterSpacing: 1.4,
    marginBottom: 14,
    marginLeft: 2,
  },
  h2: {
    letterSpacing: -0.5,
    maxWidth: 760,
  },
  lede: {
    marginTop: 14,
    maxWidth: 760,
    lineHeight: 26,
  },

  pillarsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 24,
  },
  pillarCard: {
    flexBasis: 280,
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 260,
    maxWidth: 360,
    padding: 18,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  pillarIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },

  modGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 20,
    alignContent: 'flex-start',
  },
  modCard: {
    padding: 14,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  modIcon: {
    width: 28,
    height: 28,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 10,
  },

  steps: {
    marginTop: 22,
    gap: 12,
  },
  stepCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    padding: 18,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  stepNum: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  checkGrid: {
    marginTop: 22,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
  },
  checkRow: {
    flexBasis: 320,
    maxWidth: 320,
    flexGrow: 1,
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  checkBadge: {
    width: 22,
    height: 22,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },

  compareRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
    marginTop: 22,
  },
  compareCard: {
    flexBasis: 360,
    flexGrow: 1,
    minWidth: 280,
    padding: 18,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  compareRowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingTop: 2,
  },

  faqCard: {
    padding: 16,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
  },
  faqQRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingTop: 2,
  },

  recipesBridge: {
    marginTop: 28,
    marginBottom: 8,
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
    alignItems: 'center',
    paddingHorizontal: 10,
  },
  recipesBridgeBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderRadius: BORDER_RADIUS.medium,
    borderWidth: 1,
    ...(isWeb ? { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' } : {}),
  },

  bottomCta: {
    marginTop: 60,
    width: '100%',
    maxWidth: 1100,
    alignSelf: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 18,
    padding: 24,
    borderRadius: BORDER_RADIUS.large,
    borderWidth: 1,
  },
  bottomCtaActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
});
